import * as Sentry from '@sentry/vue';
import { makeBrowserOfflineTransport, makeFetchTransport } from '@sentry/browser';
import { log } from '@/utils/core/vueLogger.js';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';

const dsn = import.meta.env.VITE_SENTRY_DSN || '';
const environment = import.meta.env.VITE_ENV || 'dev';
const release = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined;

const resolveTracesSampleRate = () => {
	const raw = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
	const parsed = Number.parseFloat(raw);
	if (Number.isFinite(parsed)) return parsed;
	return environment === 'prod' ? 0.2 : 0.1;
};

export const isSentryEnabled = () => !!dsn;

// The legacy app's boot sync fires hundreds of requests (product/account images, SFA data)
// in a tight burst; browserTracingIntegration captures each as a span on the same
// pageload/navigation transaction. Left unbounded, that transaction event can exceed
// Sentry's ~1MB per-event limit and gets rejected with a 413 ("envelope exceeded size
// limits for type 'event'"). Skip tracing binary assets and hard-cap the rest.
const MAX_TRANSACTION_SPANS = 300;
const MAX_TRANSACTION_BYTES = 900_000;
const BINARY_ASSET_URL = /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i;

const shouldCreateSpanForRequest = (url) => !BINARY_ASSET_URL.test(url);

const capTransactionSize = (event) => {
	if (!Array.isArray(event.spans)) return event;
	if (event.spans.length > MAX_TRANSACTION_SPANS) {
		event.spans = event.spans.slice(0, MAX_TRANSACTION_SPANS);
	}
	try {
		while (event.spans.length && JSON.stringify(event).length > MAX_TRANSACTION_BYTES) {
			event.spans = event.spans.slice(0, Math.floor(event.spans.length / 2));
		}
	} catch {
		// Unserializable data - let Sentry's own validation handle it
	}
	return event;
};

// WEBAPP-J: "Failed to update a ServiceWorker for scope (...): An unknown error occurred
// when fetching the script." comes from the browser's own automatic background SW update
// check (run on navigation per the Service Worker spec), not from any promise our code
// holds - every explicit `registration.update()` call in Startup.js already has a .catch().
// It's transient network/proxy noise the browser retries on its own with no user-visible
// impact (the existing service worker keeps serving), so it's filtered instead of "fixed".
const SW_UPDATE_FETCH_ERROR = /Failed to update a ServiceWorker for scope/;

// zextensions/* scripts run inside the legacy iframe but are third-party integration
// code, not our app code - tag them separately so issues don't get misattributed to
// either the 'vue' or 'legacy' source.
const ZEXTENSIONS_PATH = /zextensions/i;

const isZextensionsEvent = (event) => {
	const exceptionValues = event?.exception?.values;
	if (!exceptionValues) return false;
	return exceptionValues.some((value) => value.stacktrace?.frames?.some((frame) => frame.filename && ZEXTENSIONS_PATH.test(frame.filename)));
};

const getConnectivityStatus = () => {
	try {
		return iframeDataBridge.isOnline() ? 'online' : 'offline';
	} catch {
		return navigator.onLine ? 'online' : 'offline';
	}
};

// WEBAPP-74: "Object captured as exception with keys: isDefaultPrevented, isTrigger,
// jQuery<guid>, namespace, result, rnamespace, target, timeStamp, type" is the signature
// of a raw jQuery.Event being thrown/rejected somewhere instead of a real Error. Sentry
// already serializes the object's own properties into event.extra.__serialized__ (see
// @sentry/core eventbuilder.js -> getException), but that's buried in "Additional Data"
// and easy to miss. Promote the identifying fields to tags so the next occurrence is
// immediately actionable (searchable/groupable) without digging through raw JSON.
const RAW_EVENT_SIGNATURE_KEYS = ['isTrigger', 'rnamespace'];

const describeRawEventTarget = (target) => {
	if (typeof target === 'string') return target;
	if (!target || typeof target !== 'object') return undefined;
	const tag = target.tagName;
	if (!tag) return undefined;
	const id = target.id ? `#${target.id}` : '';
	const cls = typeof target.className === 'string' && target.className ? `.${target.className.trim().replace(/\s+/g, '.')}` : '';
	return `${tag}${id}${cls}`.slice(0, 200);
};

const tagRawEventException = (event) => {
	const serialized = event?.extra?.__serialized__;
	if (!serialized || typeof serialized !== 'object') return event;
	const looksLikeRawEvent = RAW_EVENT_SIGNATURE_KEYS.some((key) => key in serialized) || Object.keys(serialized).some((key) => /^jQuery\d+$/.test(key));
	if (!looksLikeRawEvent) return event;

	event.tags = event.tags || {};
	event.tags.errorShape = 'raw-event-thrown';
	if (typeof serialized.type === 'string') event.tags['rawEvent.type'] = serialized.type;
	if (typeof serialized.namespace === 'string' && serialized.namespace) event.tags['rawEvent.namespace'] = serialized.namespace;
	const targetDescription = describeRawEventTarget(serialized.target);
	if (targetDescription) event.tags['rawEvent.target'] = targetDescription;
	return event;
};

const applyContextTags = (event) => {
	event.tags = event.tags || {};
	event.tags.connectivityStatus = getConnectivityStatus();
	if (isZextensionsEvent(event)) {
		event.tags.source = 'extensions';
	}
	return tagRawEventException(event);
};

/**
 * Initialize Sentry for the Vue side (errors, network requests, performance).
 * No-op when VITE_SENTRY_DSN is empty so nothing is sent until a DSN is provided.
 * @param {import('vue').App} app - Vue app instance
 * @param {import('vue-router').Router} router - Vue router for navigation tracing
 */
export const initSentry = (app, router) => {
	if (!dsn) {
		log('info', 'Sentry', 'DSN not set, Sentry disabled (Vue side).');
		return;
	}

	Sentry.init({
		app,
		dsn,
		environment,
		release,
		transport: makeBrowserOfflineTransport(makeFetchTransport),
		integrations: [Sentry.browserTracingIntegration({ router, shouldCreateSpanForRequest })],
		tracesSampleRate: resolveTracesSampleRate(),
		maxBreadcrumbs: 50,
		ignoreErrors: [SW_UPDATE_FETCH_ERROR],
		beforeSend: applyContextTags,
		beforeSendTransaction: (event) => applyContextTags(capTransactionSize(event)),
		initialScope: { tags: { source: 'vue' } },
	});

	log('success', 'Sentry', `Initialized (env: ${environment}, release: ${release || 'n/a'}).`);
};

/**
 * Attach the current user and customer to the Sentry scope so issues can be
 * traced back to who hit them and at which customer.
 * @param {Object|null} user - Legacy ctx.user (firstName, lastName, userName, id)
 * @param {string|null} customerRef - ctx.customer.reference
 */
export const setSentryUser = (user, customerRef) => {
	if (!dsn || !user) return;

	Sentry.setUser({
		id: user.id,
		username: user.userName,
	});

	if (customerRef) {
		Sentry.setTag('customer.reference', customerRef);
	}
};
